// Единый маппинг ошибки API → i18n-сообщение. Вынесено из 11 инлайн-копий (DUPLICATION_AUDIT §C3).
// Два диалекта отличаются только именем поля кода и префиксом ключа:
//   full    — data.errorCode → t('error.<code>')
//   vitrine — data.error     → t('vitrine.error.<code>')

type T = (key: string) => string;

export interface MapApiErrorOpts {
  /** i18n-префикс ключа. По умолчанию 'error.' (full); vitrine — 'vitrine.error.'. */
  prefix?: string;
  /** Поле кода в response.data. По умолчанию 'errorCode' (full); vitrine — 'error'. */
  field?: string;
}

/**
 * Код из ответа → t(prefix+code), НО только если такой перевод реально существует. vue-i18n возвращает сам
 * ключ, когда перевода нет — этот путь (напр. "vitrine.error.Internal Server Error") НЕЛЬЗЯ показывать
 * пользователю, как и сырое серверное сообщение на англ. Поэтому при отсутствии конкретного перевода
 * (или если code — не наш код, а фраза статуса вроде "Internal Server Error") отдаём дружелюбный
 * переведённый UNKNOWN.
 */
export function mapApiError(e: any, t: T, opts: MapApiErrorOpts = {}): string {
  const prefix = opts.prefix ?? 'error.';
  const field = opts.field ?? 'errorCode';
  const code = e?.response?.data?.[field];
  if (code) {
    const key = `${prefix}${code}`;
    const msg = t(key);
    if (msg && msg !== key) return msg;   // конкретный перевод существует
  }
  // REQ-050: a transient failure (no response = network/timeout, or a 5xx) is NOT a client mistake — show a
  // calm "try again" instead of the generic "something went wrong", so the client retries rather than gives up.
  if (isRecoverableError(e)) {
    const rk = `${prefix}RECOVERABLE`;
    const rm = t(rk);
    if (rm && rm !== rk) return rm;
  }
  return t(`${prefix}UNKNOWN`);            // всегда переведённое сообщение, никогда путь ключа / сырой англ
}

// Transient upstream statuses worth an automatic retry: a bad gateway / unavailable / gateway timeout is
// usually a passing blip. A plain 500 is a deterministic server bug — retrying it just loops, so it is NOT here.
const RETRYABLE_STATUS = new Set([502, 503, 504]);
// Axios codes for genuine network-layer failures (no HTTP response ever arrived).
const NETWORK_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT']);

/**
 * Does the response carry a business code of ours, as opposed to a bare status?
 *
 * The distinction matters because Spring puts the status PHRASE into the same field on a plain error body —
 * `{"error": "Service Unavailable"}` — and treating that as a decision would make every genuine blip
 * non-retryable. Our codes are UPPER_SNAKE with no spaces (`TOPUP_DEPOSIT_RAIL_UNAVAILABLE`), the phrases are
 * words with spaces, so the shape tells them apart without a list to maintain.
 *
 * Both field names are checked: the vitrine passes `field: 'error'`, other callers use the default `errorCode`.
 */
function hasBusinessCode(e: any): boolean {
  const data = e?.response?.data;
  if (!data) return false;
  const candidates = [data.errorCode, data.error, data.message];
  return candidates.some(v => typeof v === 'string' && /^[A-Z][A-Z0-9_]{3,}$/.test(v.trim()));
}

/**
 * True only for a transient failure the client can simply retry: a genuine network/timeout condition (the request
 * left but no response came back) or a transient upstream status (502/503/504) — as opposed to a 4xx the client
 * must act on, a deterministic 500, or an arbitrary client-side JS exception. Lets a master-flow screen offer a
 * real retry affordance (REQ-050) without inviting an endless retry loop on a bug (D11).
 */
export function isRecoverableError(e: any): boolean {
  const status = e?.response?.status;
  // A refusal that names itself is a DECISION, not a blip: the rail answers 503 TOPUP_DEPOSIT_RAIL_UNAVAILABLE
  // when a network has no sweep target configured, and that will still be true on the next attempt. Offering
  // "try again" there sent the client into an endless loop against a permanent state, reading the whole thing
  // as a connection problem (§5 of the 16.09 run). A 502/503/504 with an EMPTY body stays retryable — that is
  // the passing-blip case this rule was written for.
  if (status != null && hasBusinessCode(e)) return false;
  if (status != null) return RETRYABLE_STATUS.has(status);   // got a response → retry only transient upstream codes
  // No response object. Only treat it as recoverable when it's a real network/timeout — an axios error whose
  // request was sent but got no reply (e.request), or a known network error code. A CORS failure or a stray JS
  // exception (no e.request, no network code) is NOT recoverable — retrying can't fix it.
  if (NETWORK_CODES.has(e?.code)) return true;
  return e?.request != null && e?.response == null && e?.isAxiosError === true;
}
